<template>
  <div class="mt-8 flex flex-col gap-y-6">
    <!-- RESUMEN GENERAL -->
    <div class="bg-white rounded-lg shadow p-6">
      <div
        v-if="loadedIndicatorsData"
        class="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4"
      >
        <CardIndicator
          title="Total de Inspecciones"
          :message="getFormatNumber(indicators.total_inspections || 0)"
        />

        <CardIndicator
          title="% Promedio de Rechazos"
          :message="avgRejectionPercentage.toFixed(2)"
        />

        <CardIndicator
          title="Cajas Rechazadas por cada 100 exportadas"
          :message="rejectedPerExported.toFixed(2)"
        />

        <CardIndicator
          title="Total Cajas Rechazadas"
          :message="getFormatNumber(indicators.total_rejected || 0)"
        >
          <slot>
            <span class="text-gray-500 text-lg font-normal">
              / {{ getFormatNumber(indicators.total_harvested || 0) }}
            </span>
          </slot>
        </CardIndicator>

        <CardIndicator
          title="Cajas promedio por inspección"
          :message="avgByInspection.toFixed(2)"
        />
      </div>
      <loading-message v-else />
    </div>

    <!-- GRÁFICOS POR CATEGORÍA DE DEFECTOS -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- CHART: DISTRIBUCIÓN DE DEFECTOS -->
      <BarChart
        class="col-span-2"
        title="Distribución de defectos"
        subtitle="(Total acumulado)"
        description=""
        :categories="this.defect_series.map(serie => serie.name)"
        :series="processedDefectSeries"
        :tooltip-formatter="val => '#' + val.toFixed(0) + ' defectos'"
        :colors="['#ef4444']"
      />

      <!-- CHART: DISTRIBUCIÓN GENERAL DE CATEGORÍAS -->
      <PieChart
        title="Distribución de Categorías de Defectos"
        subtitle="(Total acumulado)"
        description="Muestra la proporción de cada categoría de defecto en el total de defectos registrados."
        :labels="sortedDefectCategories.map(cat => cat.name)"
        :series="sortedDefectCategories.map(cat => cat.total)"
        :tooltip-formatter="val => 'Defectos: ' + val.toFixed(0)"
        :colors="['#3b82f6', '#10b981']"
      />
    </div>
  </div>
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/quality_reports/process/show_process.js";

import CardIndicator from "@/components/views/agro_exporter/quality_reports/shared/CardIndicator.vue";
import PieChart from "@/components/views/agro_exporter/quality_reports/shared/PieChart.vue";
import BarChart from "@/components/views/agro_exporter/quality_reports/shared/BarChart.vue";

export default {
  name: "QualityReportsProcessShowProcessSummary",
  components: {
    CardIndicator,
    PieChart,
    BarChart
  },
  data() {
    return {
      loadedIndicatorsData: false,
      indicators: {},
      defects: [],
      defect_series: [],
      providers_rejected_attribute_category: []
    };
  },
  props: {
    agro_exporter_id: { required: true },
    agro_exporter_process_id: { required: true }
  },
  computed: {
    avgRejectionPercentage() {
      if (!this.indicators?.total_harvested) return 0;

      return (
        (this.indicators.total_rejected / this.indicators.total_harvested) *
          100 || 0
      );
    },

    avgByInspection() {
      if (!this.indicators?.total_inspections) return 0;

      return (
        this.indicators.total_harvested / this.indicators.total_inspections
      );
    },

    rejectedPerExported() {
      if (!this.indicators.total_exported) return 0;

      return (
        (this.indicators.total_rejected / this.indicators.total_exported) *
          100 || 0
      );
    },

    // DEFECT CATEGORY ANALYSIS
    defectCategorySummary() {
      const categoriesMap = {};
      this.providers_rejected_attribute_category.forEach(provider => {
        provider.attribute_categories.forEach(category => {
          if (!categoriesMap[category.category_name]) {
            categoriesMap[category.category_name] = 0;
          }
          categoriesMap[category.category_name] += category.total_defects;
        });
      });
      return categoriesMap;
    },

    sortedDefectCategories() {
      return Object.entries(this.defectCategorySummary)
        .map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total - a.total);
    },

    processedDefectSeries: function () {
      return [
        {
          name: this.defects[0]?.name,
          data: this.defect_series.map(serie => serie.data[0])
        }
      ];
    }
  },

  methods: {
    async fetchIndicators() {
      const { data } = await Requester.fetchIndicators(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.indicators = data;
      this.loadedIndicatorsData = true;
    },

    fetchTopDefects: async function (provider_id = null) {
      let response = await Requester.fetchTopDefects(
        this.agro_exporter_id,
        this.agro_exporter_process_id,
        provider_id
      );
      this.defects = response.data.defects;
      this.defect_series = response.data.provider_series;
    },

    async fetchProvidersRejectedAttributeCategory() {
      const { data } = await Requester.fetchProvidersRejectedAttributeCategory(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.providers_rejected_attribute_category = data;
    }
  },

  created() {
    this.fetchIndicators();
    this.fetchTopDefects();
    this.fetchProvidersRejectedAttributeCategory();
  }
};
</script>

<style scoped>
.apexcharts-tooltip-custom {
  @apply bg-gray-800 text-white p-2 rounded text-sm;
}
</style>
